'use client';
import { AssistantResponseFooter } from './AssistantResponseFooter';
import { AssistantActivity } from './AssistantActivity';
import { AssistantWorkingMark } from './AssistantWorkingMark';
import { AssistantPageContextSummary } from '@/app/Components/Assistant/AssistantPageContextSummary';

import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useRouter } from 'next/navigation';
import { AssistantRuntimeProvider, AttachmentPrimitive, ComposerPrimitive, MessagePartPrimitive, MessagePrimitive, ThreadPrimitive, useAui, useAuiEvent, useAuiState, type ToolCallMessagePartProps } from '@assistant-ui/react';
import dynamic from 'next/dynamic';
import { fetchCompleteAssistantStream } from './completeStream';
import { AssistantChatTransport, useAISDKChat, useChatRuntime } from '@assistant-ui/ai-sdk';
import type { UIMessage } from 'ai';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArchiveBoxIcon, ArrowTopRightOnSquareIcon, ArrowUpIcon, CalendarDaysIcon, ChatBubbleLeftRightIcon, CheckIcon, ChevronDownIcon, DocumentTextIcon, GlobeAltIcon, PaperClipIcon, PlusIcon, PuzzlePieceIcon, TableCellsIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/app/Components/AuthProvider';
import { OperationalState, OperationalStatus } from '@/app/Components/OperationalPage';
import { CourseListRenderer } from '@/app/Components/Assistant/renderers/CourseListRenderer';
import { ScheduleResultsRenderer } from '@/app/Components/Assistant/renderers/ScheduleResultsRenderer';
import { AssistantSafeLink, withAssistantMentions } from './AssistantMentionText';
import {
  AssistantDegreePlanVisual,
  AssistantDegreeVisual,
  AssistantDiningVisual,
  AssistantEventVisual,
  AssistantParkingVisual,
  AssistantProfessorVisual,
  AssistantResearchVisual,
  AssistantRoomVisual,
  AssistantScheduleVisual,
  AssistantTransitVisual,
} from './AssistantCapabilityVisuals';
import { cachedGetSession, supabase } from '@/app/lib/supabase';
import { parseScheduleArtifact, type ScheduleArtifact } from './scheduleArtifact';
import { assistantAttachmentAdapter, validatePendingAssistantAttachments } from '@/app/lib/assistant-v2/attachments';
import { filesFromClipboardData } from '@/app/lib/assistant-v2/clipboardFiles';
import { exactAssistantSlashCommand, filterAssistantSlashCommands, assistantSlashQuery, type AssistantSlashCommandId } from '@/app/lib/assistant-v2/slashCommands';
import { applyAssistantMention, assistantMentionQuery, filterAssistantMentions, mentionSelectionOverrides, retainMentionedInText, ASSISTANT_MENTION_KIND_LABELS, type AssistantMention, type AssistantMentionCandidate } from '@/app/lib/assistant-v2/mentions';
import { useAssistantMentionCandidates } from './useAssistantMentionCandidates';
import { assistantRunPresentationSummary, type AssistantRunPresentation, type AssistantThinkingKey } from '@/app/lib/assistant-v2/runPresentation';
import { readAssistantThinkingPreference, writeAssistantThinkingPreference } from '@/app/lib/assistant-v2/thinkingPreference';
import { ConversationHistory, type AssistantConversationSummary } from '../ConversationHistory';
import { SkillLibrary } from './SkillLibrary';
import { prepareSkillDraft } from './skillLibraryData';
import { AssistantConnectorsDialog } from './AssistantConnectorsDialog';
import { parseStructuredArtifact, type StructuredAssistantArtifact } from '@/app/lib/assistant-v2/artifacts/structured';
import { StructuredArtifactPane } from './StructuredArtifactPane';
import { ArtifactLibraryPane } from './ArtifactLibraryPane';
import { parseStoredAssistantArtifact, type AssistantArtifactLibraryItem } from '@/app/lib/assistant-v2/artifacts/library';
import { useResponsivePaneDialog } from '../useResponsivePaneDialog';
import { FollowUpComposer } from './FollowUpComposer';
import { AssistantDictation, type AssistantDictationHandle } from './AssistantDictation';
const AssistantGeneratedPanel = dynamic(() => import('./AssistantGeneratedPanel').then(module => module.AssistantGeneratedPanel));

import type { AssistantSelectedEntity, AssistantVisibleFact } from '@/app/lib/assistant-v2/context';

type StoredAssistantMessage = {
  id?: string;
  role?: unknown;
  parts?: unknown;
  content?: unknown;
};

type WorkspaceHandoff = {
  selectedScheduleId?: string;
  selectedDegreePlanId?: string;
  sourceRoute?: string;
  activeTerm?: string;
  selectedCourseIds?: string[];
  visibleSectionIds?: string[];
  selectedEntity?: AssistantSelectedEntity;
  availablePageActions?: string[];
  visibleView?: AssistantVisibleFact[];
};

function formatRetrievedAt(value: unknown): string {
  const date = new Date(String(value ?? ''));
  return Number.isNaN(date.getTime()) ? 'Retrieved recently' : `Retrieved ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)}`;
}

function sourceHostname(value: unknown): string {
  try { return new URL(String(value)).hostname.replace(/^www\./, ''); }
  catch { return 'Public source'; }
}

const workingToolLabels: Record<string, string> = {
  get_student_context: 'Reading your academic context',
  search_course_sections: 'Checking course sections',
  generate_schedule_options: 'Building schedule options',
  search_web: 'Checking current sources',
  preview_webpage: 'Reading an official source',
  search_skills: 'Finding the right assistant skill',
  read_skill: 'Loading assistant guidance',
  present_ui: 'Preparing interactive answer',
  create_document_artifact: 'Preparing your document',
  create_spreadsheet_artifact: 'Preparing your spreadsheet',
  save_schedule: 'Saving your schedule',
  search_degrees: 'Finding degree requirements',
  check_degree_progress: 'Checking degree progress',
  get_selected_degree_plan: 'Reading your degree plan',
  find_available_rooms: 'Checking room availability',
  search_professors: 'Finding instructors',
};

export type AssistantWorkStep = { key: string; label: string; state: 'complete' | 'active' };

function assistantToolName(part: unknown): string | undefined {
  if (!part || typeof part !== 'object') return undefined;
  const record = part as { type?: unknown; toolName?: unknown };
  const type = typeof record.type === 'string' ? record.type : '';
  return typeof record.toolName === 'string'
    ? record.toolName
    : type.startsWith('tool-') && type !== 'tool-call'
      ? type.slice(5)
      : undefined;
}

export function assistantWorkSteps(messages: ReadonlyArray<{ role?: unknown; parts?: readonly unknown[] }>): AssistantWorkStep[] {
  const steps = new Map<string, AssistantWorkStep>();
  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    for (const part of message.parts ?? []) {
      let toolName = assistantToolName(part);
      if (!toolName) continue;
      const record = part as { args?: unknown; state?: unknown; status?: { type?: unknown } };
      if (toolName === 'execute_capability' && record.args && typeof record.args === 'object' && 'capabilityId' in record.args) {
        toolName = String((record.args as { capabilityId?: unknown }).capabilityId ?? toolName);
      }
      const status = typeof record.state === 'string' ? record.state : typeof record.status?.type === 'string' ? record.status.type : '';
      const complete = status === 'output-available' || status === 'complete';
      steps.set(toolName, { key: toolName, label: workingToolLabels[toolName] ?? 'Using Scarlet Sync data', state: complete ? 'complete' : 'active' });
    }
  }
  return [...steps.values()];
}

const transcriptColumnClassName = 'mx-auto w-full max-w-3xl px-4 sm:px-6';

const visibleToolArgumentKeys: Record<string, readonly string[]> = {
  search_course_sections: ['query', 'department', 'campus', 'semester', 'openOnly', 'limit'],
  search_skills: ['query'],
  read_skill: ['skillId', 'version'],
  search_tools: ['query', 'skillId'],
  execute_capability: ['capabilityId'],
  search_web: ['query', 'officialOnly'],
  preview_webpage: ['url'],
  generate_schedule_options: ['courseBaseIds', 'semester', 'earliestStartMinute', 'latestEndMinute', 'preferredCampuses', 'openOnly', 'maxSchedules'],
};

export function visibleAssistantToolArguments(toolName: string, args: unknown): Record<string, unknown> | null {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return null;
  const allowed = visibleToolArgumentKeys[toolName];
  if (!allowed) return null;
  const record = args as Record<string, unknown>;
  const visible = Object.fromEntries(allowed.flatMap((key) => key in record ? [[key, record[key]]] : []));
  return Object.keys(visible).length ? visible : null;
}

export function latestAssistantWorkLabel(messages: ReadonlyArray<{ role?: unknown; parts?: readonly unknown[] }>): string {
  const current = messages.at(-1);
  if (current?.role !== 'assistant') return 'Thinking';
  const parts = current.parts ?? [];
  for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
    const part = parts[partIndex];
    if (!part || typeof part !== 'object') continue;
    let toolName = assistantToolName(part);
    if (toolName === 'execute_capability' && 'args' in part && part.args && typeof part.args === 'object' && 'capabilityId' in part.args) {
      toolName = String((part.args as { capabilityId?: unknown }).capabilityId ?? toolName);
    }
    if (toolName) return workingToolLabels[toolName] ?? 'Using Scarlet Sync data';
  }
  return 'Thinking';
}

export function continueInterruptedResponse(thread: { getState: () => { messages: ReadonlyArray<{ id: string }> }; resumeRun: (config: { parentId: string | null }) => unknown }): void {
  const parentId = thread.getState().messages.at(-1)?.id ?? null;
  thread.resumeRun({ parentId });
}

type AssistantChatResumeApi = {
  messages: ReadonlyArray<{ id: string; role?: string; parts?: readonly unknown[] }>;
  regenerate: (options?: { messageId?: string }) => Promise<void>;
  setMessages?: (messages: UIMessage[]) => void;
};

type QueuedFollowUp = { id: string; text: string };

export function nextFollowUpAfterRun(input: { manualStop: boolean; steeringText?: string; queue: QueuedFollowUp[] }): { text?: string; remaining: QueuedFollowUp[] } {
  if (input.manualStop) return { remaining: [] };
  if (input.steeringText) return { text: input.steeringText, remaining: input.queue };
  const [next, ...remaining] = input.queue;
  return { text: next?.text, remaining };
}

export function hasLaterUserTurn(messages: ReadonlyArray<{ id?: string; role?: unknown }>, messageId?: string): boolean {
  if (!messageId) return false;
  const messageIndex = messages.findIndex((message) => message.id === messageId);
  return messageIndex >= 0 && messages.slice(messageIndex + 1).some((message) => message.role === 'user');
}

export async function retryInterruptedResponse(chat: AssistantChatResumeApi | null): Promise<void> {
  if (!chat) throw new Error('The assistant retry channel is not ready');
  const last = chat.messages.at(-1);
  // A start event can create a local assistant shell that the server never saves.
  const target = last?.role === 'assistant' && last.parts?.length === 0
    ? chat.messages.findLast((message) => message.role === 'user')
    : last;
  const messageId = target?.id;
  await chat.regenerate(messageId ? { messageId } : undefined);
}

function AssistantChatResumeBridge({ chatRef }: { chatRef: MutableRefObject<AssistantChatResumeApi | null> }) {
  const chat = useAISDKChat();
  useEffect(() => {
    chatRef.current = chat ? { messages: chat.messages, setMessages: (messages) => chat.setMessages(messages), regenerate: (options) => chat.regenerate(options) } : null;
    return () => { chatRef.current = null; };
  }, [chat, chatRef]);
  return null;
}

export function ActiveRunFollowUps({ onManualStop, onSteer }: { onManualStop: () => void; onSteer: () => void }) {
  const aui = useAui();
  const isRunning = useAuiState((state) => state.thread.isRunning);
  const [queue, setQueue] = useState<QueuedFollowUp[]>([]);
  const queueRef = useRef(queue);
  const steeringRef = useRef<string | undefined>(undefined);
  const manualStopRef = useRef(false);
  const wasRunningRef = useRef(isRunning);
  queueRef.current = queue;

  useLayoutEffect(() => {
    if (isRunning) {
      wasRunningRef.current = true;
      return;
    }
    if (!wasRunningRef.current) return;
    wasRunningRef.current = false;
    const next = nextFollowUpAfterRun({ manualStop: manualStopRef.current, steeringText: steeringRef.current, queue: queueRef.current.filter(item => item.text.trim()) });
    manualStopRef.current = false;
    steeringRef.current = undefined;
    setQueue(next.remaining);
    if (!next.text) return;
    aui.composer.setText(next.text);
    aui.composer.send();
  }, [aui, isRunning]);

  const enqueue = (draft: string) => {
    const text = draft.trim();
    if (!text) return;
    const next = [...queueRef.current, { id: crypto.randomUUID(), text }];
    queueRef.current = next;
    setQueue(next);
  };
  const steer = (text: string, queuedId?: string) => {
    const guidance = text.trim();
    if (!guidance) return;
    steeringRef.current = guidance;
    if (queuedId) {
      queueRef.current = queueRef.current.filter(item => item.id !== queuedId);
      setQueue(queueRef.current);
    }
    onSteer();
    aui.thread.cancelRun();
  };
  const stop = () => {
    manualStopRef.current = true;
    setQueue([]);
    onManualStop();
    aui.thread.cancelRun();
  };

  if (!isRunning && queue.length === 0) return null;
  return (
    <div className={`${transcriptColumnClassName} space-y-2`} role="region" aria-label="Follow-up queue">
      {queue.length ? <section aria-label="Queued follow-ups" className="max-h-40 overflow-y-auto rounded-control bg-[var(--ss-surface-subtle)] p-2">
        <p className="px-2 text-xs font-semibold text-[var(--ss-text-muted)]">{queue.length} queued</p>
        <ol>{queue.map((item, index) => <li key={item.id} className="flex items-center gap-1 border-b border-[var(--ss-border)]">
          <textarea aria-label={`Queued message ${index+1}`} value={item.text} rows={1} onChange={event => { const next = queueRef.current.map(current => current.id===item.id ? {...current,text:event.target.value} : current); queueRef.current=next;setQueue(next); }} className="min-h-12 min-w-0 flex-1 resize-y bg-transparent p-2 text-sm" />
          <button type="button" disabled={!item.text.trim()} onClick={()=>steer(item.text,item.id)} aria-label={`Send queued message ${index+1} now`} title="Send now" className="grid size-12 shrink-0 place-items-center rounded-control text-[var(--ss-ai-strong)] disabled:opacity-40"><ArrowUpIcon className="size-4"/></button>
          <button type="button" onClick={()=>{queueRef.current=queueRef.current.filter(candidate=>candidate.id!==item.id);setQueue(queueRef.current);}} aria-label={`Delete queued follow-up ${index+1}`} className="grid size-12 shrink-0 place-items-center rounded-control text-[var(--ss-text-muted)]"><XMarkIcon className="size-4"/></button>
        </li>)}</ol>
      </section> : null}
      {isRunning ? <FollowUpComposer onQueue={enqueue} onSteer={steer} onStop={stop} hasQueue={queue.length>0}/> : null}
    </div>
  );
}

function normalizedStoredParts(message: StoredAssistantMessage): UIMessage['parts'] {
  const raw = message.parts ?? message.content;
  if (typeof raw === 'string') return [{ type: 'text', text: raw }];
  if (!Array.isArray(raw)) return [];
  return raw.filter((part): part is UIMessage['parts'][number] => Boolean(part && typeof part === 'object' && typeof (part as { type?: unknown }).type === 'string'));
}

function toUIMessage(message: StoredAssistantMessage, index: number): UIMessage {
  return {
    id: message.id?.trim() ? message.id : `stored-${index}`,
    role: message.role as 'user' | 'assistant',
    parts: normalizedStoredParts(message),
  };
}

export function normalizeStoredAssistantMessages(messages: StoredAssistantMessage[]): UIMessage[] {
  const visibleMessages = messages.filter((message) => message.role === 'user' || message.role === 'assistant');
  return visibleMessages.map((message, index) => {
    const hasLaterUserTurn = visibleMessages.slice(index + 1).some((candidate) => candidate.role === 'user');
    const parts = normalizedStoredParts(message);
    const normalizedParts = hasLaterUserTurn
      ? parts.filter((part) => {
          if (part.type !== 'data-run-status') return true;
          const data = (part as { data?: { status?: unknown } }).data;
          return data?.status !== 'interrupted';
        })
      : parts;
    return toUIMessage({ ...message, parts: normalizedParts }, index);
  });
}

export function findReusableEmptyConversation(rows: Array<{ id: string; title: string | null; messages?: unknown }>): string | undefined {
  return rows.find((row) => row.title === 'New conversation' && Array.isArray(row.messages) && row.messages.length === 0)?.id;
}

function workspaceParams(threadId: string): URLSearchParams {
  return new URLSearchParams({ threadId });
}

const ArtifactWorkspaceContext = createContext<{
  openArtifact: (artifact: ScheduleArtifact | StructuredAssistantArtifact) => void;
} | null>(null);
const ActivityVisibilityContext = createContext(false);

export function ScheduleArtifactPane({ artifact, onClose }: { artifact: ScheduleArtifact; onClose: () => void }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { paneRef, isMobileOverlay, onKeyDown } = useResponsivePaneDialog(onClose, headingRef);
  return (
    <aside
      ref={paneRef}
      className="absolute inset-0 z-20 flex min-h-0 flex-col bg-[var(--ss-surface)] md:static md:z-auto md:w-[clamp(22rem,42%,40rem)] md:border-l md:border-[var(--ss-border)]"
      aria-labelledby="assistant-artifact-title"
      role={isMobileOverlay ? 'dialog' : 'region'}
      aria-modal={isMobileOverlay ? true : undefined}
      onKeyDown={onKeyDown}
    >
      <header className="flex items-start justify-between gap-3 border-b border-[var(--ss-border)] px-4 py-4 sm:px-5">
        <div className="min-w-0">
          <p className="operational-eyebrow">Schedule artifact</p>
          <h2 ref={headingRef} tabIndex={-1} id="assistant-artifact-title" className="mt-1 truncate text-lg font-semibold text-[var(--ss-text)] outline-none">
            {artifact.title}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <OperationalStatus tone="info">Generated draft</OperationalStatus>
            {artifact.semester ? <span className="text-xs font-medium text-[var(--ss-text-muted)]">{artifact.semester}</span> : null}
          </div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close schedule artifact" className="grid size-12 shrink-0 place-items-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
          <XMarkIcon className="size-5" aria-hidden="true" />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-5" data-release-scroll-reset>
        <p className="text-sm leading-6 text-[var(--ss-text-muted)]">{artifact.summary}</p>
        {artifact.options.length > 0 ? (
          <div className="mt-5">
            <ScheduleResultsRenderer
              data={{
                schedules: artifact.options.map((option) => ({
                  ...option,
                  score: typeof option.qualityScore === 'number' ? Math.round(option.qualityScore * 10) : undefined,
                })),
                totalFound: artifact.options.length,
                status: 'ready',
              }}
              metadata={{ semester: artifact.semester }}
              showCreditLoadStatus={false}
            />
          </div>
        ) : (
          <div className="mt-5 rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-4">
            <p className="text-sm font-semibold text-[var(--ss-text)]">Saved schedule options</p>
            <p className="mt-1 text-xs leading-5 text-[var(--ss-text-muted)]">Open the originating conversation to regenerate the detailed schedule view.</p>
          </div>
        )}
        <details className="mt-5 rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-3 text-xs text-[var(--ss-text-muted)]">
          <summary className="min-h-11 cursor-pointer content-center font-semibold text-[var(--ss-text)]">Technical details</summary>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words leading-5">
            {JSON.stringify(
              {
                artifactId: artifact.artifactId,
                artifactType: artifact.artifactType,
                optionIds: artifact.optionIds,
              },
              null,
              2,
            )}
          </pre>
        </details>
      </div>
    </aside>
  );
}

export function ToolCallView({ toolName, args, result, status, approval, respondToApproval, showActivityOverride, openArtifactOverride }: ToolCallMessagePartProps & { showActivityOverride?: boolean; openArtifactOverride?: (artifact: ScheduleArtifact | StructuredAssistantArtifact) => void }) {
  const artifactWorkspace = useContext(ArtifactWorkspaceContext);
  const activityVisibility = useContext(ActivityVisibilityContext);
  const showActivity = showActivityOverride ?? activityVisibility;
  const openArtifact = openArtifactOverride ?? artifactWorkspace?.openArtifact;
  const artifact = useMemo(() => parseScheduleArtifact(result), [result]);
  const structuredArtifact = useMemo(() => parseStructuredArtifact(result), [result]);
  const resultData = typeof result === 'object' && result && 'data' in result && typeof (result as { data?: unknown }).data === 'object' ? (result as { data: Record<string, unknown> }).data : null;
  const webSources = Array.isArray(resultData?.results) ? resultData.results : Array.isArray(resultData?.suggestions) ? resultData.suggestions : [];
  const capabilityId = typeof args === 'object' && args && 'capabilityId' in args
    ? String((args as { capabilityId?: unknown }).capabilityId ?? '')
    : toolName === 'search_web' || toolName === 'preview_webpage'
      ? toolName
      : '';
  const resultMetadata = typeof result === 'object' && result && 'metadata' in result && typeof (result as { metadata?: unknown }).metadata === 'object' ? (result as { metadata: Record<string, unknown> }).metadata : null;
  const count = Array.isArray(resultData?.courses) ? resultData.courses.length : Array.isArray(resultData?.schedules) ? resultData.schedules.length : null;
  const waitingForApproval = approval && approval.approved === undefined;
  const isCourseResult = toolName === 'search_course_sections' && Array.isArray(resultData?.courses);
  const isWebSearchResult = capabilityId === 'search_web' && webSources.length > 0;
  const isWebPreviewResult = capabilityId === 'preview_webpage' && typeof resultData?.url === 'string';
  const isDegreeSearchResult = capabilityId === 'search_degrees' && Array.isArray(resultData?.degrees);
  const isDegreeProgressResult = capabilityId === 'check_degree_progress' && Boolean(resultData?.degree || resultData?.progress);
  const isDegreePlanResult = capabilityId === 'get_selected_degree_plan' && Boolean(resultData?.name || resultData?.plan);
  const isTransitResult = capabilityId === 'best_bus_to_get_to_campus' && Boolean(resultData?.stop || resultData?.arrivals);
  const isDiningResult = capabilityId === 'what_can_i_eat_now' && Array.isArray(resultData?.halls);
  const isEventResult = capabilityId === 'find_events_for_free_time' && Array.isArray(resultData?.events);
  const isParkingResult = capabilityId === 'can_i_park_today' && Array.isArray(resultData?.lots);
  const isRoomResult = capabilityId === 'find_available_rooms' && Array.isArray(resultData?.rooms);
  const isProfessorResult = capabilityId === 'search_professors' && (Array.isArray(resultData?.professors) || Array.isArray(resultData?.instructors));
  const isResearchResult = capabilityId === 'search_research_opportunities' && (Array.isArray(resultData?.opportunities) || Array.isArray(resultData?.results));
  const failed = status.type === 'incomplete' || Boolean(result && typeof result === 'object' && 'success' in result && (result as { success?: unknown }).success === false);
  const isSaveOutcome = toolName === 'save_schedule' && !waitingForApproval && (status.type === 'complete' || approval?.approved === false || failed);
  const saveRejected = isSaveOutcome && approval?.approved === false;
  const isSelectedScheduleResult = toolName === 'get_selected_schedule' && Boolean(resultData?.schedule_id);
  const generatedSchedules = toolName === 'generate_schedule_options' && Array.isArray(resultData?.schedules) ? resultData.schedules : [];
  const hasCapabilityVisual = isDegreeSearchResult || isDegreeProgressResult || isDegreePlanResult || isTransitResult || isDiningResult || isEventResult || isParkingResult || isRoomResult || isProfessorResult || isResearchResult || generatedSchedules.length > 0;
  const generatedArtifact = generatedSchedules.length > 0
    ? {
        artifactId: `transient-${toolName}`,
        artifactType: 'scarlet.schedule-options.v1',
        title: 'Generated schedule options',
        summary: `${generatedSchedules.length} ranked Rutgers schedule option${generatedSchedules.length === 1 ? '' : 's'} ready to compare.`,
        optionIds: generatedSchedules.map((_, index) => `option-${index + 1}`),
        options: generatedSchedules,
        semester: typeof args === 'object' && args && 'semester' in args ? String(args.semester) : undefined,
      } satisfies ScheduleArtifact
    : null;
  const visibleArtifact = artifact ?? structuredArtifact ?? generatedArtifact;
  if (toolName === 'present_ui') {
    if (status.type === 'incomplete') return <p role="status">Interactive response could not finish. Ask Scarlet to retry.</p>;
    if (status.type !== 'complete') return <p role="status" className="text-sm text-[var(--ss-text-muted)]">Preparing interactive answer…</p>;
    return <AssistantGeneratedPanel data={resultData} />;
  }
  const visibleArguments = visibleAssistantToolArguments(toolName, args);
  const privateTool = toolName === 'get_student_context' || toolName === 'get_selected_schedule' || toolName === 'save_schedule';
  const toolLabel: Record<string, string> = {
    search_course_sections: 'Course section search',
    get_selected_schedule: 'Read selected schedule',
    get_student_context: 'Read student context',
    generate_schedule_options: 'Generate schedule options',
    create_schedule_artifact: 'Create schedule artifact',
    save_schedule: 'Save schedule',
    search_skills: 'Discover skills',
    read_skill: 'Read skill',
    search_tools: 'Discover capabilities',
    execute_capability: 'Run capability',
  };

  if (!visibleArtifact && !waitingForApproval && !isCourseResult && !isWebSearchResult && !isWebPreviewResult && !isSelectedScheduleResult && !isSaveOutcome && !hasCapabilityVisual && !showActivity) return null;

  return (
    <div className="my-3 text-sm text-[var(--ss-text)]">
      {(isCourseResult || isWebSearchResult || isWebPreviewResult || isSelectedScheduleResult || hasCapabilityVisual) ? <details className="my-2 text-sm text-[var(--ss-text-muted)]">
        <summary className="flex min-h-12 cursor-pointer items-center gap-2 rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">View results · {toolLabel[toolName] ?? toolName.replaceAll('_', ' ')}</summary>
        <div className="max-h-[50dvh] overflow-y-auto overscroll-contain text-[var(--ss-text)]">
      {isCourseResult ? (
        <div className="overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)]">
          <CourseListRenderer data={resultData} metadata={{ ...resultMetadata, source: 'Rutgers course catalog' }} />
        </div>
      ) : null}
      {isWebSearchResult ? (
        <section aria-label={resultData?.sourceStatus === 'suggested-not-retrieved' ? 'Suggested official sources' : 'Sources consulted'} className="overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)]">
          <header className="flex items-start gap-3 border-b border-[var(--ss-border)] px-4 py-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-control bg-[var(--ss-ai-bg)] text-[var(--ss-ai)]"><GlobeAltIcon className="size-5" aria-hidden="true" /></span>
            <div className="min-w-0"><p className="font-semibold">{resultData?.sourceStatus === 'suggested-not-retrieved' ? 'Suggested official sources' : 'Sources consulted'}</p><p className="mt-1 text-xs text-[var(--ss-text-muted)]">{resultData?.sourceStatus === 'suggested-not-retrieved' ? 'Curated directory · Open a source to verify current details' : `${formatRetrievedAt(resultData?.retrievedAt)} · ${Boolean(resultData?.officialOnly) ? 'Official Rutgers sources' : 'Public web sources'}`}</p></div>
          </header>
          <ul className="divide-y divide-[var(--ss-border)]">
            {(webSources as Array<Record<string, unknown>>).slice(0, 6).map((source, index) => (
              <li key={String(source.url ?? index)}>
                <a href={String(source.url)} target="_blank" rel="noreferrer" className="flex min-h-16 items-start gap-3 px-4 py-3 hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)]">
                  <span className="grid size-7 shrink-0 place-items-center rounded-control bg-[var(--ss-ai-bg)] text-[var(--ss-ai)]" aria-label={`Source: ${sourceHostname(source.url)}`}><GlobeAltIcon className="size-4" aria-hidden="true" /></span>
                  <div className="min-w-0 flex-1"><p className="font-semibold text-[var(--ss-text)]">{String(source.title ?? source.url)}</p>{source.snippet ? <p className="mt-1 line-clamp-2 text-xs leading-5 text-[var(--ss-text-muted)]">{String(source.snippet)}</p> : null}<p className="mt-1 truncate text-xs font-medium text-[var(--ss-brand)]">{sourceHostname(source.url)}</p></div>
                  <ArrowTopRightOnSquareIcon className="mt-1 size-4 shrink-0 text-[var(--ss-text-muted)]" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {isWebPreviewResult ? (
        <section aria-label="Webpage preview" className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4">
          <div className="flex items-start gap-3"><span className="grid size-10 shrink-0 place-items-center rounded-control bg-[var(--ss-ai-bg)] text-[var(--ss-ai)]"><DocumentTextIcon className="size-5" aria-hidden="true" /></span><div className="min-w-0 flex-1"><p className="operational-eyebrow">Public webpage</p><p className="mt-1 font-semibold">{String(resultData?.title ?? resultData?.url)}</p><p className="mt-1 line-clamp-3 text-xs leading-5 text-[var(--ss-text-muted)]">{String(resultData?.description ?? resultData?.text ?? '')}</p><a href={String(resultData?.url)} target="_blank" rel="noreferrer" className="mt-3 inline-flex min-h-12 items-center gap-2 font-semibold text-[var(--ss-brand)] hover:underline">Open source <ArrowTopRightOnSquareIcon className="size-4" aria-hidden="true" /></a></div></div>
        </section>
      ) : null}
      {isSelectedScheduleResult ? (
        <div className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="operational-eyebrow">Saved schedule</p>
              <p className="mt-1 font-semibold text-[var(--ss-text)]">{String(resultData?.name ?? 'Selected schedule')}</p>
              <p className="mt-1 text-xs text-[var(--ss-text-muted)]">{String(resultData?.term_id ?? 'Current term')} · {Array.isArray(resultData?.classes) ? resultData.classes.length : 0} sections</p>
            </div>
            <a href="/scheduling" className="inline-flex min-h-12 items-center rounded-control border border-[var(--ss-border)] px-4 font-semibold text-[var(--ss-brand)] hover:bg-[var(--ss-brand-bg)]">Open in Scheduling</a>
          </div>
        </div>
      ) : null}
      {generatedSchedules.length > 0 ? <div className="mt-3"><AssistantScheduleVisual schedules={generatedSchedules} /></div> : null}
      {isDegreeSearchResult || isDegreeProgressResult ? <div className="mt-3"><AssistantDegreeVisual data={resultData} /></div> : null}
      {isDegreePlanResult ? <div className="mt-3"><AssistantDegreePlanVisual data={resultData} /></div> : null}
      {isTransitResult ? <div className="mt-3"><AssistantTransitVisual data={resultData} /></div> : null}
      {isDiningResult ? <div className="mt-3"><AssistantDiningVisual data={resultData} /></div> : null}
      {isEventResult ? <div className="mt-3"><AssistantEventVisual data={resultData} /></div> : null}
      {isParkingResult ? <div className="mt-3"><AssistantParkingVisual data={resultData} /></div> : null}
      {isRoomResult ? <div className="mt-3"><AssistantRoomVisual data={resultData} /></div> : null}
      {isProfessorResult ? <div className="mt-3"><AssistantProfessorVisual data={resultData} /></div> : null}
      {isResearchResult ? <div className="mt-3"><AssistantResearchVisual data={resultData} /></div> : null}
        </div>
      </details> : null}
      {(showActivity || waitingForApproval || isSaveOutcome || visibleArtifact) ? (
      <div className={`${isCourseResult ? 'mt-3 ' : ''}rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-3`}>
      {showActivity || waitingForApproval ? (
        <details className="group" open={status.type !== 'complete'}>
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] [&::-webkit-details-marker]:hidden">
            <span className="font-mono text-base text-[var(--ss-ai)]" aria-hidden="true">{'{}'}</span>
            <span className="font-medium text-[var(--ss-text-muted)]">{status.type === 'complete' ? 'Completed' : waitingForApproval ? 'Approval needed' : 'Calling'}</span>
            <span className="min-w-0 flex-1 font-semibold">{toolLabel[toolName] ?? toolName}</span>
            <OperationalStatus tone={status.type === 'complete' ? 'positive' : waitingForApproval ? 'warning' : 'neutral'}>{status.type === 'complete' ? 'Done' : waitingForApproval ? 'Approval' : 'Running'}</OperationalStatus>
            <ChevronDownIcon className="size-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
          </summary>
          <div className="mt-2 border-t border-[var(--ss-border)] pt-3">
            {visibleArguments ? <pre className="max-h-56 overflow-auto rounded-control bg-[var(--ss-surface)] p-3 text-xs leading-5 text-[var(--ss-text)]">{JSON.stringify(visibleArguments, null, 2)}</pre> : <p className="text-xs leading-5 text-[var(--ss-text-muted)]">{privateTool ? 'Private student or protected-action details are hidden.' : 'No public inputs to show.'}</p>}
            {count !== null ? <p className="mt-2 text-[var(--ss-text-muted)]">{count} {toolName === 'generate_schedule_options' ? `option${count === 1 ? '' : 's'} generated` : `section${count === 1 ? '' : 's'} returned`}</p> : null}
          </div>
        </details>
      ) : null}
      {visibleArtifact ? (
        <div className="mt-3 border-t border-[var(--ss-border)] pt-3">
          <div className="flex gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-control bg-[var(--ss-ai-bg)] text-[var(--ss-ai)]">
              {visibleArtifact.artifactType === 'scarlet.schedule-options.v1' ? <CalendarDaysIcon className="size-5" aria-hidden="true" /> : visibleArtifact.artifactType === 'scarlet.document.v1' ? <DocumentTextIcon className="size-5" aria-hidden="true" /> : <TableCellsIcon className="size-5" aria-hidden="true" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[var(--ss-text)]">{visibleArtifact.title}</p>
              <p className="mt-1 line-clamp-2 text-xs leading-5 text-[var(--ss-text-muted)]">{visibleArtifact.summary}</p>
            </div>
          </div>
          <button type="button" onClick={() => openArtifact?.(visibleArtifact)} className="mt-3 min-h-12 w-full rounded-control border border-[var(--ss-ai)] px-4 font-semibold text-[var(--ss-ai)] hover:bg-[var(--ss-ai-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
            Open artifact
          </button>
        </div>
      ) : null}
      {waitingForApproval ? (
        <div className="mt-3 border-t border-[var(--ss-border)] pt-3">
          <p className="font-semibold">Confirm schedule replacement</p>
          <p className="mt-1 text-xs leading-5 text-[var(--ss-text-muted)]">Scarlet only updates confirmed AI-only drafts automatically. This saved schedule is not confirmed as AI-only, so replacing it needs your permission.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] p-3 text-xs leading-5 text-[var(--ss-text-muted)]">
              <span className="operational-eyebrow block">Current schedule</span>
              <span className="mt-1 block font-semibold text-[var(--ss-text)]">Stays unchanged until you approve</span>
              <span className="mt-1 block">Review its current courses and meeting times before deciding.</span>
              {typeof args === 'object' && args && 'scheduleId' in args && args.scheduleId ? <a href={`/scheduling?scheduleId=${encodeURIComponent(String(args.scheduleId))}`} className="mt-2 inline-flex min-h-11 items-center font-semibold text-[var(--ss-brand)] hover:underline">Review in Scheduling</a> : null}
            </div>
            <div className="rounded-control border border-[var(--ss-brand)] bg-[var(--ss-brand-bg)] p-3 text-xs leading-5 text-[var(--ss-text-muted)]">
              <span className="operational-eyebrow block">Proposed replacement</span>
              <span className="mt-1 block font-semibold text-[var(--ss-text)]">{typeof args === 'object' && args && 'name' in args ? String(args.name) : 'Saved schedule'}</span>
              <span className="block">{typeof args === 'object' && args && 'termId' in args ? String(args.termId) : 'Current term'}</span>
              <span className="block">{typeof args === 'object' && args && 'sectionIndexes' in args && Array.isArray(args.sectionIndexes) ? `${args.sectionIndexes.length} proposed section${args.sectionIndexes.length === 1 ? '' : 's'}` : 'Proposed sections available'}</span>
              <span className="mt-1 block break-words">Indexes: {typeof args === 'object' && args && 'sectionIndexes' in args && Array.isArray(args.sectionIndexes) ? args.sectionIndexes.join(', ') : 'See schedule'}</span>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => respondToApproval({ approved: false })} className="min-h-12 rounded-control border border-[var(--ss-border)] px-4 font-medium text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
              Keep current schedule
            </button>
            <button type="button" onClick={() => respondToApproval({ approved: true })} className="min-h-12 rounded-control bg-[var(--ss-brand)] px-4 font-semibold text-[var(--ss-brand-contrast)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
              Allow changes
            </button>
          </div>
        </div>
      ) : null}
      {isSaveOutcome ? (
        <div role={failed ? 'alert' : 'status'} className="flex items-start justify-between gap-3">
          <div>
            <p className="font-semibold text-[var(--ss-text)]">{saveRejected ? 'Schedule kept unchanged' : failed ? 'Schedule was not saved' : 'Schedule saved'}</p>
            <p className="mt-1 text-xs leading-5 text-[var(--ss-text-muted)]">
              {saveRejected
                ? 'Scarlet did not apply the proposed changes.'
                : failed
                  ? 'The requested change could not be completed. Your existing schedule is unchanged.'
                  : `${typeof args === 'object' && args && 'name' in args ? String(args.name) : 'Your schedule'} is available in Scheduling.`}
            </p>
          </div>
          <OperationalStatus tone={saveRejected ? 'neutral' : failed ? 'critical' : 'positive'}>{saveRejected ? 'Not changed' : failed ? 'Failed' : 'Saved'}</OperationalStatus>
        </div>
      ) : null}
      </div>
      ) : null}
    </div>
  );
}

function UserMessage() {
  return (
    <MessagePrimitive.Root className={`${transcriptColumnClassName} flex justify-end py-3`}>
      <div className="max-w-[85%] rounded-card bg-[var(--ss-brand)] px-4 py-3 text-sm leading-6 text-[var(--ss-brand-contrast)] sm:max-w-[75%]">
        <MessagePrimitive.Attachments components={{ Attachment: SentAttachment }} />
        <MessagePrimitive.Parts />
      </div>
    </MessagePrimitive.Root>
  );
}

function SentAttachment() {
  return <AttachmentPrimitive.Root className="mb-2 flex items-center gap-2 rounded-control bg-white/12 px-3 py-2 text-xs"><PaperClipIcon className="size-4 shrink-0" aria-hidden="true" /><span className="min-w-0 truncate"><AttachmentPrimitive.Name /></span></AttachmentPrimitive.Root>;
}

function ComposerAttachment() {
  return (
    <AttachmentPrimitive.Root className="flex max-w-full items-center gap-2 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-3 py-2 text-xs text-[var(--ss-text)]">
      <PaperClipIcon className="size-4 shrink-0 text-[var(--ss-text-muted)]" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate"><AttachmentPrimitive.Name /></span>
      <AttachmentPrimitive.Remove aria-label="Remove attachment" className="grid size-8 shrink-0 place-items-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface)]"><XMarkIcon className="size-4" aria-hidden="true" /></AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}

function ComposerAttachmentFeedback() {
  const attachments = useAuiState((state) => state.composer.attachments);
  const [adapterError, setAdapterError] = useState<string>();
  useAuiEvent('composer.attachmentAddError', ({ message }) => setAdapterError(message));
  useAuiEvent('composer.attachmentAdd', () => setAdapterError(undefined));
  const limitError = validatePendingAssistantAttachments(attachments);
  const message = limitError ?? adapterError;
  return message ? <p role="alert" className="px-2 text-xs font-medium text-[var(--ss-critical)]">{message}</p> : null;
}

function ComposerSkillLibrary({ onClose }: { onClose: () => void }) {
  const aui = useAui();
  return <SkillLibrary onClose={onClose} onSelect={(skill) => {
    aui.composer.setText(prepareSkillDraft(skill, aui.composer.getState().text));
    onClose();
  }} />;
}

function ComposerPlusMenu({ workspaceOverlayOpen = false, onOpenSkills }: { workspaceOverlayOpen?: boolean; onOpenSkills: () => void }) {
  const [open, setOpen] = useState(false);
  const popupId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const uploadRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (workspaceOverlayOpen) setOpen(false);
  }, [workspaceOverlayOpen]);
  useEffect(() => {
    if (!open) return;
    const dismissOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const dismissWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissWithEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissWithEscape);
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => uploadRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);
  return (
    <div ref={menuRef} className="relative shrink-0">
      <button ref={triggerRef} type="button" aria-label="Add files or skills" aria-haspopup="dialog" aria-controls={popupId} aria-expanded={open} onClick={() => setOpen((value) => !value)} className="grid size-12 place-items-center rounded-control text-[var(--ss-text)] hover:bg-[var(--ss-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"><PlusIcon className="size-5" aria-hidden="true" /></button>
      {open ? (
        <div id={popupId} role="dialog" aria-label="Add to conversation" className="absolute bottom-14 left-0 z-30 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-e3">
          <div className="border-b border-[var(--ss-border)] p-2">
            <ComposerPrimitive.AddAttachment ref={uploadRef} aria-label="Upload files" title="Add images, PDFs, or text files" className="flex min-h-12 w-full items-center gap-3 rounded-control px-3 text-left text-sm font-semibold text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)]" onClick={() => setOpen(false)}><PaperClipIcon className="size-5 text-[var(--ss-text-muted)]" aria-hidden="true" /><span>Upload files<span className="mt-0.5 block text-xs font-normal text-[var(--ss-text-muted)]">Up to 3 files · 5 MB each · 8 MB total</span></span></ComposerPrimitive.AddAttachment>
          </div>
          <div className="p-2">
            <button type="button" onClick={() => { triggerRef.current?.focus(); setOpen(false); onOpenSkills(); }} className="flex min-h-12 w-full items-center gap-3 rounded-control px-3 text-left text-sm font-semibold text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"><PuzzlePieceIcon className="size-5 text-[var(--ss-ai)]" aria-hidden="true" />Browse skills library</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RunModelPart({ data }: { data?: AssistantRunPresentation }) {
  if (!data?.modelLabel) return null;
  return <p className="mt-2 text-xs text-[var(--ss-text-muted)]">{assistantRunPresentationSummary(data)}</p>;
}

const THINKING_CHOICES = [
  { key: 'default' as const, label: 'Standard', hint: 'Fast answers for ordinary questions' },
  { key: 'reasoning' as const, label: 'Deeper', hint: 'More budget for hard planning questions' },
];

/**
 * Thinking level as a compact chip beside Send, rather than a segmented control on
 * its own row: it is a setting the student changes rarely, so it should cost width,
 * not vertical space in the composer.
 */
function ThinkingChip({ value, onChange }: { value: AssistantThinkingKey; onChange: (value: AssistantThinkingKey) => void }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const active = THINKING_CHOICES.find((choice) => choice.key === value) ?? THINKING_CHOICES[0];
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => menuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus());
    const onPointer = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node) && !triggerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
      const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? []);
      if (items.length && ['ArrowDown','ArrowUp','Home','End'].includes(event.key)) {
        event.preventDefault();
        const current = items.indexOf(document.activeElement as HTMLButtonElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length-1 : (current + (event.key==='ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
      }
      if (event.key === 'Tab') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => { cancelAnimationFrame(frame); document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Thinking level: ${active.label}`}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex min-h-[48px] items-center gap-1 rounded-control px-[8px] text-xs font-semibold text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
      >
        {active.label}
        <ChevronDownIcon className={`size-4 text-[var(--ss-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open ? (
        <div ref={menuRef} role="menu" aria-label="Thinking level" className="absolute bottom-full left-2 z-20 mb-2 w-64 overflow-hidden rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-e2">
          {THINKING_CHOICES.map((choice) => (
            <button
              key={choice.key}
              type="button"
              role="menuitemradio"
              aria-checked={choice.key === value}
              onClick={() => { onChange(choice.key); setOpen(false); triggerRef.current?.focus(); }}
              className={`flex min-h-[48px] w-full flex-col items-start px-[12px] py-2 text-left hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${choice.key === value ? 'bg-[var(--ss-surface-subtle)]' : ''}`}
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold text-[var(--ss-text)]">
                {choice.key === value ? <CheckIcon className="size-4 text-[var(--ss-ai)]" aria-hidden="true" /> : <span className="size-4" aria-hidden="true" />}
                {choice.label}
              </span>
              <span className="pl-[1.375rem] text-xs text-[var(--ss-text-muted)]">{choice.hint}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Openers for an empty thread. They are phrased as things a student would actually
 * type, and they fill the composer rather than sending: the first turn is usually
 * worth a word of editing, and an auto-sent prompt spends a run the student did
 * not choose.
 */
const ASSISTANT_STARTERS: readonly { label: string; prompt: string }[] = [
  { label: 'Plan next semester', prompt: 'What should I take next semester? Use my saved plan and what I have already completed.' },
  { label: 'Check my progress', prompt: 'Am I on track to graduate on time? Walk through what is left and where the risk is.' },
  { label: 'Build me a schedule', prompt: 'Build me a Fall 2026 schedule with no classes before 10am and as few cross-campus hops as possible.' },
  { label: 'Check a course before I register', prompt: 'Check prerequisites, open sections, and instructor ratings for a course before I register.' },
];

function StartingPrompts() {
  const aui = useAui();
  return (
    <div className="mx-auto mt-4 w-full max-w-3xl">
      <div className="flex flex-wrap justify-center gap-2">
        {ASSISTANT_STARTERS.slice(0, 2).map((starter) => (
          <button
            key={starter.label}
            type="button"
            onClick={() => aui.composer.setText(starter.prompt)}
            className="min-h-12 rounded-control border border-[var(--ss-border)] px-3 py-2 text-left text-sm font-medium text-[var(--ss-text)] transition hover:border-[var(--ss-ai)] hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
          >
            {starter.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ComposerMentionMenu({
  candidates,
  loading,
  active,
  onActive,
  onSelect,
}: {
  candidates: AssistantMentionCandidate[];
  loading: boolean;
  active: number;
  onActive: (index: number) => void;
  onSelect: (candidate: AssistantMentionCandidate) => void;
}) {
  if (loading && !candidates.length) {
    return <p role="status" className="rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] px-3 py-2 text-xs text-[var(--ss-text-muted)]">Loading your saved work…</p>;
  }
  if (!candidates.length) {
    return <p role="status" className="rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] px-3 py-2 text-xs text-[var(--ss-text-muted)]">No saved schedule, degree plan, or course list matches that.</p>;
  }
  return (
    <div role="listbox" aria-label="Your saved work" className="max-h-64 overflow-y-auto rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-e2">
      {candidates.map((candidate, index) => (
        <button
          key={`${candidate.kind}:${candidate.id}`}
          type="button"
          role="option"
          aria-selected={index === active}
          onMouseEnter={() => onActive(index)}
          onClick={() => onSelect(candidate)}
          className={`flex min-h-12 w-full flex-col items-start px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${index === active ? 'bg-[var(--ss-surface-subtle)]' : ''}`}
        >
          <span className="text-sm font-semibold text-[var(--ss-text)]">@{candidate.label}</span>
          <span className="text-xs text-[var(--ss-text-muted)]">{[ASSISTANT_MENTION_KIND_LABELS[candidate.kind], candidate.detail].filter(Boolean).join(' · ')}</span>
        </button>
      ))}
    </div>
  );
}

function ComposerSlashMenu({
  commands,
  active,
  onActive,
  onSelect,
}: {
  commands: ReturnType<typeof filterAssistantSlashCommands>;
  active: number;
  onActive: (index: number) => void;
  onSelect: (id: AssistantSlashCommandId) => void;
}) {
  if (!commands.length) return null;
  return (
    <div role="listbox" aria-label="Assistant commands" className="overflow-hidden rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-e2">
      {commands.map((command, index) => (
        <button
          key={command.id}
          type="button"
          role="option"
          aria-selected={index === active}
          onMouseEnter={() => onActive(index)}
          onClick={() => onSelect(command.id)}
          className={`flex min-h-12 w-full flex-col items-start px-3 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${index === active ? 'bg-[var(--ss-surface-subtle)]' : ''}`}
        >
          <span className="text-sm font-semibold text-[var(--ss-text)]">/{command.aliases[0]} · {command.title}</span>
          <span className="text-xs text-[var(--ss-text-muted)]">{command.description}</span>
        </button>
      ))}
    </div>
  );
}

function ComposerDock({
  compact,
  dictationKey,
  thinkingKey,
  onThinking,
  workspaceOverlayOpen,
  onOpenSkills,
  onOpenConnectors,
  onSubmitStart,
  onMention,
}: {
  compact: boolean;
  dictationKey: string;
  thinkingKey: AssistantThinkingKey;
  onThinking: (value: AssistantThinkingKey) => void;
  workspaceOverlayOpen: boolean;
  onOpenSkills: () => void;
  onOpenConnectors: () => void;
  onSubmitStart: (text: string) => void;
  onMention: (mention: AssistantMention) => void;
}) {
  const aui = useAui();
  const dictationRef = useRef<AssistantDictationHandle>(null);
  const text = useAuiState((state) => state.composer.text);
  const commands = filterAssistantSlashCommands(text);
  const mentionQuery = assistantMentionQuery(text);
  const { candidates, loading: mentionsLoading, load: loadMentions } = useAssistantMentionCandidates();
  const mentions = mentionQuery === null ? [] : filterAssistantMentions(candidates, mentionQuery);
  const menuLength = mentionQuery === null ? commands.length : mentions.length;
  const [active, setActive] = useState(0);
  useEffect(() => { setActive(0); }, [text]);
  useEffect(() => { if (mentionQuery !== null) void loadMentions(); }, [mentionQuery, loadMentions]);
  const applyMention = (candidate: AssistantMentionCandidate) => {
    aui.composer.setText(applyAssistantMention(aui.composer.getState().text, candidate));
    onMention({ kind: candidate.kind, id: candidate.id, label: candidate.label });
  };
  const applyCommand = (id: AssistantSlashCommandId) => {
    aui.composer.setText('');
    if (id === 'skills') onOpenSkills();
    if (id === 'standard') onThinking('default');
    if (id === 'deeper') onThinking('reasoning');
    if (id === 'connectors') onOpenConnectors();
  };
  const applySlashOrSend = (event: { preventDefault: () => void }) => {
    if (mentionQuery !== null && mentions[active]) {
      event.preventDefault();
      applyMention(mentions[active]);
      return;
    }
    const exact = exactAssistantSlashCommand(aui.composer.getState().text);
    const selected = exact ?? commands[active];
    if (selected && assistantSlashQuery(aui.composer.getState().text) !== null) {
      event.preventDefault();
      applyCommand(selected.id);
      return;
    }
    dictationRef.current?.cancel();
    onSubmitStart(aui.composer.getState().text);
  };
  return (
    <ComposerPrimitive.Root onSubmit={applySlashOrSend} className="relative mx-auto flex w-full max-w-3xl flex-col gap-2 rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] p-[8px] shadow-e1 focus-within:ring-2 focus-within:ring-[var(--ss-focus)]">
      <div className="flex flex-wrap gap-2 px-1"><ComposerPrimitive.Attachments components={{ Attachment: ComposerAttachment }} /></div>
      <ComposerAttachmentFeedback />
      {mentionQuery === null
        ? <ComposerSlashMenu commands={commands} active={active} onActive={setActive} onSelect={applyCommand} />
        : <ComposerMentionMenu candidates={mentions} loading={mentionsLoading} active={active} onActive={setActive} onSelect={applyMention} />}
      <div className="ss-assistant-composer-row flex flex-wrap items-center gap-2">
        <ComposerPlusMenu workspaceOverlayOpen={workspaceOverlayOpen} onOpenSkills={onOpenSkills} />
        <ComposerPrimitive.Input
          autoFocus={compact}
          aria-label="Ask Scarlet Sync"
          placeholder="Ask Scarlet Sync…"
          className="order-first h-auto min-h-[56px] max-h-[160px] w-full min-w-0 resize-y bg-transparent px-[8px] py-[12px] text-base leading-6 text-[var(--ss-text)] outline-none placeholder:text-[var(--ss-text-muted)]"
          rows={1}
          onKeyDown={(event) => {
            if (!menuLength) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActive((index) => (index + 1) % menuLength);
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((index) => (index - 1 + menuLength) % menuLength);
            }
            if (event.key === 'Tab' && mentionQuery !== null && mentions[active]) {
              event.preventDefault();
              applyMention(mentions[active]);
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              aui.composer.setText('');
            }
          }}
          onPaste={(event) => {
            if (event.clipboardData?.files.length) return;
            const files = filesFromClipboardData(event.clipboardData);
            if (!files.length) return;
            event.preventDefault();
            void Promise.all(files.map((file) => aui.composer.addAttachment(file)));
          }}
        />
        <div className="mr-auto"><ThinkingChip value={thinkingKey} onChange={onThinking} /></div>
        <AssistantDictation key={dictationKey} ref={dictationRef} getDraft={() => aui.composer.getState().text} setDraft={(value) => aui.composer.setText(value)} />
        <ComposerPrimitive.Send onClick={applySlashOrSend} aria-label="Send message" className="grid size-[48px] shrink-0 place-items-center rounded-control bg-[var(--ss-ai)] text-white outline-none transition-colors hover:bg-[var(--ss-ai-strong)] focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] disabled:cursor-not-allowed disabled:opacity-50">
          <ArrowUpIcon className="size-5" aria-hidden="true" />
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
}

export function describeAssistantTransportError(error: Error): string {
  const message = error.message.toLowerCase();
  if (message.includes('stream ended before completion')) return 'The connection ended before the answer arrived. Load the saved response before starting another run.';
  if (message.includes('401') || message.includes('unauthorized') || message.includes('bearer')) {
    return 'Your sign-in session was not accepted. Refresh the page, then retry.';
  }
  if (message.includes('403') || message.includes('forbidden')) {
    return 'This account is not allowed to use the assistant.';
  }
  if (message.includes('429') || message.includes('rate limit')) {
    return 'The model provider is temporarily at capacity. Retry shortly.';
  }
  if (message.includes('failed to fetch') || message.includes('network')) {
    return 'The assistant backend could not be reached. Check the connection, then retry.';
  }
  return 'The request did not complete. Retry when you are ready.';
}

export function RunStatusNotice({ status, onContinue, detail, onReload }: { status?: string; onContinue: () => void; detail?: string; onReload?: () => void }) {
  if (status !== 'interrupted' && status !== 'transport-failed') return null;
  const transportFailed = status === 'transport-failed';
  return (
    <div role={transportFailed ? 'alert' : 'status'} data-interrupted-status className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-3">
      <div><p className="text-sm font-semibold text-[var(--ss-text)]">{transportFailed ? 'Response could not finish' : 'Response stopped'}</p><p className="mt-1 text-xs leading-5 text-[var(--ss-text-muted)]">{transportFailed ? detail ?? 'The request did not complete. Retry when you are ready.' : 'The partial answer was saved. Retry the response from your last message.'}</p></div>
      {onReload ? <button type="button" onClick={onReload} className="min-h-12 rounded-control border border-[var(--ss-border)] px-4 text-sm font-semibold">Load saved response</button> : null}
      <button type="button" onClick={onContinue} className="min-h-12 rounded-control border border-[var(--ss-ai)] px-4 text-sm font-semibold text-[var(--ss-ai)] hover:bg-[var(--ss-ai-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Retry response</button>
    </div>
  );
}

function RunStatusPart({ data }: { data?: { status?: string } }) {
  const aui = useAui();
  const hasBeenSuperseded = useAuiState((state) => hasLaterUserTurn(state.thread.messages, state.message.id));
  if (hasBeenSuperseded) return null;
  return <RunStatusNotice status={data?.status} onContinue={() => continueInterruptedResponse(aui.thread)} />;
}

function LiveInterruptedNotice({ onContinue }: { onContinue: () => void }) {
  const aui = useAui();
  return <RunStatusNotice status="interrupted" onContinue={() => {
    onContinue();
    continueInterruptedResponse(aui.thread);
  }} />;
}

const ResponseTimestampContext = createContext<Record<string, string>>({});
const RunStartContext = createContext<number | null>(null);

function AssistantRunClock({ children }: { children: React.ReactNode }) {
  const running = useAuiState(state => state.thread.isRunning);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  useLayoutEffect(() => { setStartedAt(running ? Date.now() : null); }, [running]);
  return <RunStartContext.Provider value={startedAt}>{children}</RunStartContext.Provider>;
}

function RunningStatus() {
  const messages = useAuiState((state) => state.thread.messages);
  const runStart = useContext(RunStartContext);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    const startedAt = runStart ?? Date.now();
    const timer = window.setInterval(() => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1_000)), 1_000);
    return () => window.clearInterval(timer);
  }, [runStart]);
  const latestLabel = latestAssistantWorkLabel(messages);
  const steps = assistantWorkSteps(messages);
  const preparingAnswer = steps.length > 0 && steps.every((step) => step.state === 'complete');
  const label = preparingAnswer ? 'Preparing your answer' : latestLabel;
  const visibleSteps = preparingAnswer ? [...steps, { key: 'answer', label, state: 'active' as const }] : steps;
  // Once the response shell exists, its activity row owns timing and receipts.
  if (messages.at(-1)?.role === 'assistant') return null;
  return <AssistantRunStatusPresentation label={label} elapsedSeconds={elapsedSeconds} steps={visibleSteps} />;
}

export function AssistantRunStatusPresentation({ label, elapsedSeconds, steps }: { label: string; elapsedSeconds: number; steps: AssistantWorkStep[] }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <div className={`${transcriptColumnClassName} py-3`}>
      <div className="text-sm text-[var(--ss-text-muted)]">
        <div className="flex items-center gap-2">
          <AssistantWorkingMark />
          <span role="status" className="font-medium text-[var(--ss-text)]">{label}</span>
          <span className="tabular-nums">· Working for {elapsedSeconds}s</span>
          <button type="button" aria-expanded={detailsOpen} onClick={() => setDetailsOpen(value => !value)} className="ml-auto inline-flex min-h-12 sm:min-h-10 items-center gap-1 rounded-control px-2 text-xs font-medium text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
            {detailsOpen ? 'Hide steps' : 'View steps'} <ChevronDownIcon className={`size-4 ${detailsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
        </div>
        {detailsOpen ? (
          <div className="mt-2 border-l border-[var(--ss-border)] pl-4 text-xs">
            <ol className="mt-2 space-y-2" aria-label="Assistant activity">
              {(steps.length ? steps : [{ key: 'model', label, state: 'active' as const }]).map((step) => (
                <li key={step.key} className="flex items-center gap-2">
                  {step.state === 'complete' ? <span className="grid size-5 shrink-0 place-items-center rounded-full bg-[var(--ss-positive-bg)] text-[var(--ss-positive)]"><CheckIcon className="size-3.5" aria-hidden="true" /></span> : <span className="size-2 shrink-0 rounded-full bg-[var(--ss-ai)]" aria-hidden="true" />}
                  <span>{step.label}</span>
                  <span className="ml-auto font-medium">{step.state === 'complete' ? 'Done' : 'In progress'}</span>
                </li>
              ))}
            </ol>
            <p className="mt-2 leading-5">Shows actions and data lookups only. Private context and internal reasoning stay hidden.</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function AssistantMarkdownText({ text }: { text: string }) {
  return (
    <div className="prose prose-sm max-w-none break-words text-[var(--ss-text)] prose-headings:text-[var(--ss-text)] prose-p:my-1 prose-p:leading-6 prose-li:my-0.5 prose-a:font-medium prose-a:text-[var(--ss-link)] prose-a:underline prose-a:underline-offset-2 dark:prose-invert">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) => <AssistantSafeLink href={href}>{children}</AssistantSafeLink>,
          p: ({ children }) => <p>{withAssistantMentions(children)}</p>,
          li: ({ children }) => <li>{withAssistantMentions(children)}</li>,
          td: ({ children }) => <td>{withAssistantMentions(children)}</td>,
          th: ({ children }) => <th>{withAssistantMentions(children)}</th>,
          h1: ({ children }) => <h1>{withAssistantMentions(children)}</h1>,
          h2: ({ children }) => <h2>{withAssistantMentions(children)}</h2>,
          h3: ({ children }) => <h3>{withAssistantMentions(children)}</h3>,
          img: ({ alt }) => <span>{alt ?? 'Image'}</span>,
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function AssistantMessage() {
  const startedAt = useContext(RunStartContext);
  const responseTimes = useContext(ResponseTimestampContext);
  const messageId = useAuiState(state => state.message.id);
  const parts = useAuiState(state => state.message.content);
  const status = useAuiState(state => state.message.status);
  return (
    <MessagePrimitive.Root className={`${transcriptColumnClassName} py-3`}>
        <div className="min-w-0 text-sm leading-6 text-[var(--ss-text)]">
          <AssistantActivity startedAt={status?.type === 'running' ? startedAt : undefined} parts={parts} running={status?.type === 'running'} interrupted={status?.type === 'incomplete'} />
          <MessagePrimitive.Parts components={{ Text: AssistantMarkdownText, data: { by_name: { 'run-status': RunStatusPart, 'run-model': RunModelPart } }, tools: { Fallback: ToolCallView } }} />
          <AssistantResponseFooter text={parts.flatMap(part => part.type === 'text' ? [part.text] : []).join('\n\n')} running={status?.type === 'running'} timestamp={responseTimes[messageId]} />
      </div>
    </MessagePrimitive.Root>
  );
}

function InitialPromptBridge({ prompt, autoSend, onConsumed }: { prompt?: string; autoSend?: boolean; onConsumed: () => void }) {
  const aui = useAui();
  const handledRef = useRef(false);
  useEffect(() => {
    if (!prompt || handledRef.current) return;
    handledRef.current = true;
    aui.composer.setText(prompt);
    if (autoSend) window.setTimeout(() => aui.composer.send(), 0);
    onConsumed();
  }, [aui, autoSend, onConsumed, prompt]);
  return null;
}

function RunBusyBridge({ onChange }: { onChange?: (busy: boolean) => void }) {
  const busy = useAuiState(state => state.thread.isRunning);
  useEffect(() => { onChange?.(busy); }, [busy, onChange]);
  return null;
}

function AssistantThreadRoot({ children, compact }: { children: React.ReactNode; compact: boolean }) {
  const empty = useAuiState(state => state.thread.isEmpty);
  return <ThreadPrimitive.Root data-assistant-chat data-empty={empty || undefined} data-compact={compact || undefined} className="relative flex min-h-0 min-w-0 flex-1 flex-col">{children}</ThreadPrimitive.Root>;
}

export function AssistantThread({ compact = false, onBusyChange, threadId, initialMessages, initialPrompt, autoSend, selectedScheduleId, selectedDegreePlanId, sourceRoute, activeTerm, selectedCourseIds, visibleSectionIds, selectedEntity, availablePageActions, visibleView, artifacts, artifactError, onOpenHistory, onArtifactsChanged, onConversationsChanged, onPromptConsumed }: WorkspaceHandoff & { compact?: boolean; onBusyChange?: (busy: boolean) => void; threadId: string; initialMessages: UIMessage[]; initialPrompt?: string; autoSend?: boolean; artifacts: AssistantArtifactLibraryItem[]; artifactError?: string; onOpenHistory: () => void; onArtifactsChanged: () => void; onConversationsChanged: () => void; onPromptConsumed: () => void }) {
  const chatResumeRef = useRef<AssistantChatResumeApi | null>(null);
  const [responseTimes, setResponseTimes] = useState<Record<string, string>>({});
  const [activeArtifact, setActiveArtifact] = useState<ScheduleArtifact | StructuredAssistantArtifact | null>(null);
  const [artifactLibraryOpen, setArtifactLibraryOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [connectorsOpen, setConnectorsOpen] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [interruptedLocally, setInterruptedLocally] = useState(false);
  const [transportFailure, setTransportFailure] = useState<string | null>(null);
  const [thinkingKey, setThinkingKey] = useState<AssistantThinkingKey>('default');
  const thinkingKeyRef = useRef<AssistantThinkingKey>('default');
  // Mentions are read at send time, not at transport construction time, so a
  // mention picked after the composer mounted still reaches the run.
  const mentionsRef = useRef<AssistantMention[]>([]);
  const rememberMention = useCallback((mention: AssistantMention) => {
    mentionsRef.current = [...mentionsRef.current.filter((existing) => !(existing.kind === mention.kind && existing.id === mention.id)), mention].slice(-12);
  }, []);
  useEffect(() => {
    const value = readAssistantThinkingPreference();
    thinkingKeyRef.current = value;
    setThinkingKey(value);
  }, []);
  const setThinking = (value: AssistantThinkingKey) => {
    thinkingKeyRef.current = value;
    setThinkingKey(value);
    writeAssistantThinkingPreference(value);
  };
  const toggleActivity = () => setShowActivity((value) => !value);
  const artifactWorkspace = useMemo(() => ({ openArtifact: (artifact: ScheduleArtifact | StructuredAssistantArtifact) => {
    setArtifactLibraryOpen(false);
    setActiveArtifact(artifact);
  } }), []);
  const workspaceOverlayOpen = skillsOpen || connectorsOpen || artifactLibraryOpen || Boolean(activeArtifact);
  const transport = useMemo(
    () =>
      new AssistantChatTransport({
        api: '/api/assistant/v2',
        fetch: fetchCompleteAssistantStream,
        headers: async (): Promise<Record<string, string>> => {
          const session = await cachedGetSession(0);
          return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
        },
        body: {
          threadId,
          get modelKey() { return thinkingKeyRef.current; },
          get context() {
            const mentions = mentionsRef.current;
            return {
              route: sourceRoute ?? '/ai-assistant',
              ...(activeTerm ? { activeTerm } : {}),
              ...(selectedScheduleId ? { selectedScheduleId } : {}),
              ...(selectedDegreePlanId ? { selectedDegreePlanId } : {}),
              ...(selectedCourseIds?.length ? { selectedCourseIds } : {}),
              ...(visibleSectionIds?.length ? { visibleSectionIds } : {}),
              ...(selectedEntity ? { selectedEntity } : {}),
              ...(visibleView?.length ? { visibleView } : {}),
              // A named schedule or plan outranks whatever page happened to hand off.
              ...mentionSelectionOverrides(mentions),
              ...(mentions.length ? { mentions } : {}),
              availablePageActions: availablePageActions ?? [],
            };
          },
        },
      }),
    [activeTerm, availablePageActions, selectedCourseIds, selectedDegreePlanId, selectedEntity, selectedScheduleId, sourceRoute, threadId, visibleSectionIds, visibleView],
  );
  const runtime = useChatRuntime({
    id: threadId,
    messages: initialMessages,
    transport,
    onResume: async () => retryInterruptedResponse(chatResumeRef.current),
    onError: (error) => setTransportFailure(describeAssistantTransportError(error)),
    onFinish: ({ message, isAbort, isError }) => {
      if (!isAbort && !isError) setResponseTimes(times => ({ ...times, [message.id]: new Date().toISOString() }));
      if (!isAbort && !isError) setInterruptedLocally(false);
      if (!isError) setTransportFailure(null);
      onArtifactsChanged();
      onConversationsChanged();
    },
    adapters: { attachments: assistantAttachmentAdapter },
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <AssistantRunClock>
      <ResponseTimestampContext.Provider value={responseTimes}>
      <AssistantChatResumeBridge chatRef={chatResumeRef} />
      <RunBusyBridge onChange={onBusyChange} />
      {skillsOpen ? <ComposerSkillLibrary onClose={() => setSkillsOpen(false)} /> : null}
      <AssistantConnectorsDialog open={connectorsOpen} onClose={() => setConnectorsOpen(false)} />
      <InitialPromptBridge prompt={initialPrompt} autoSend={autoSend} onConsumed={onPromptConsumed} />
      <ArtifactWorkspaceContext.Provider value={artifactWorkspace}>
        <ActivityVisibilityContext.Provider value={showActivity}>
          <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
            <AssistantThreadRoot compact={compact}>
              <div data-assistant-toolbar className="relative flex shrink-0 items-center justify-between px-3 py-1">
                  <button type="button" onClick={onOpenHistory} aria-label="Open conversation history" className={`grid size-12 place-items-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] ${compact ? '' : 'md:hidden'}`}><ChatBubbleLeftRightIcon className="size-5" aria-hidden="true" /></button>
                <details className="group ml-auto" data-assistant-tools>
                  <summary className="flex min-h-12 cursor-pointer list-none items-center gap-2 rounded-control px-3 text-sm text-[var(--ss-text-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">Tools <ChevronDownIcon className="size-4" aria-hidden="true" /></summary>
                  <div className="absolute right-3 top-full z-20 flex max-h-[50dvh] w-64 max-w-[calc(100vw-2rem)] flex-col overflow-y-auto rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] p-2 shadow-e2">
                  <button type="button" onClick={() => setSkillsOpen(true)} aria-haspopup="dialog" className="inline-flex min-h-12 items-center gap-2 rounded-control px-3 text-xs font-semibold text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"><PuzzlePieceIcon className="size-4" aria-hidden="true" />Skills</button>
                  <button type="button" aria-pressed={artifactLibraryOpen} onClick={() => { setActiveArtifact(null); setArtifactLibraryOpen((value) => !value); }} className="inline-flex min-h-12 items-center gap-2 rounded-control px-3 text-xs font-semibold text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]">
                    <ArchiveBoxIcon className="size-4" aria-hidden="true" />
                    Artifacts{artifacts.length ? ` (${artifacts.length})` : ''}
                  </button>
                    <button type="button" onClick={() => setConnectorsOpen(true)} className="min-h-12 rounded-control px-3 text-left text-sm text-[var(--ss-text)]">Integrations</button>
                    <button type="button" aria-pressed={showActivity} onClick={toggleActivity} className="min-h-12 rounded-control px-3 text-left text-sm text-[var(--ss-text)]">{showActivity ? 'Hide activity' : 'Show activity'}</button>
                  </div>
                </details>
              </div>
              <ThreadPrimitive.Viewport data-assistant-transcript className="min-h-0 flex-1 overflow-y-auto overscroll-contain" turnAnchor="top">
                <ThreadPrimitive.Empty>
                  <div className={`${transcriptColumnClassName} flex justify-center py-6 text-center`}>
                    <div>

                      <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[var(--ss-text)]">Where should we begin?</h2>


                    </div>
                  </div>
                </ThreadPrimitive.Empty>
                <ThreadPrimitive.Messages components={{ UserMessage, AssistantMessage }} />
                <ThreadPrimitive.If running>
                  <RunningStatus />
                </ThreadPrimitive.If>
                {interruptedLocally ? (
                  <div className={transcriptColumnClassName}>
                    <LiveInterruptedNotice onContinue={() => setInterruptedLocally(false)} />
                  </div>
                ) : null}
                {transportFailure ? (
                  <div className={transcriptColumnClassName}>
                    <RunStatusNotice status="transport-failed" onContinue={() => {
                      setTransportFailure(null);
                      void retryInterruptedResponse(chatResumeRef.current).catch((error: unknown) => setTransportFailure(describeAssistantTransportError(error instanceof Error ? error : new Error('Unknown transport error'))));
                    }} onReload={() => {
                      const chat = chatResumeRef.current;
                      const lastUserId = chat?.messages.findLast(message => message.role === 'user')?.id;
                      void supabase.from('ai_conversations').select('messages').eq('id', threadId).single().then(({ data, error }) => {
                        const messages = normalizeStoredAssistantMessages(Array.isArray(data?.messages) ? data.messages : []);
                        const last = messages.at(-1);
                        if (!error && lastUserId && messages.findLast(message => message.role === 'user')?.id === lastUserId && last?.role === 'assistant' && last.parts.some(part => part.type === 'text' && part.text.trim()) && !last.parts.some(part => part.type === 'data-run-status') && chatResumeRef.current?.messages.findLast(message => message.role === 'user')?.id === lastUserId) {
                          chatResumeRef.current?.setMessages?.(messages);
                          setTransportFailure(null);
                        } else setTransportFailure('A completed saved response is not available yet. Try again shortly.');
                      });
                    }} detail={transportFailure} />
                  </div>
                ) : null}
              </ThreadPrimitive.Viewport>
              <footer data-assistant-composer-dock className="shrink-0 bg-[var(--ss-surface)] px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 sm:px-4 sm:pb-3">
                {compact ? <AssistantPageContextSummary sourceRoute={sourceRoute} activeTerm={activeTerm} selectedScheduleId={selectedScheduleId} selectedDegreePlanId={selectedDegreePlanId} selectedCourseIds={selectedCourseIds} visibleSectionIds={visibleSectionIds} selectedEntity={selectedEntity} availablePageActions={availablePageActions} visibleView={visibleView} /> : null}
                <ThreadPrimitive.If running={false}>
                  <ComposerDock
                    compact={compact}
                    dictationKey={threadId}
                    thinkingKey={thinkingKey}
                    onThinking={setThinking}
                    workspaceOverlayOpen={workspaceOverlayOpen}
                    onOpenSkills={() => setSkillsOpen(true)}
                    onOpenConnectors={() => setConnectorsOpen(true)}
                    onSubmitStart={(outgoing) => {
                      setInterruptedLocally(false);
                      setTransportFailure(null);
                      mentionsRef.current = retainMentionedInText(mentionsRef.current, outgoing);
                    }}
                    onMention={rememberMention}
                  />
                </ThreadPrimitive.If>
                <ThreadPrimitive.Empty><StartingPrompts /></ThreadPrimitive.Empty>
                <ActiveRunFollowUps onManualStop={() => setInterruptedLocally(true)} onSteer={() => setInterruptedLocally(false)} />
              </footer>
            </AssistantThreadRoot>
            {artifactLibraryOpen ? <ArtifactLibraryPane items={artifacts} error={artifactError} onOpen={(artifact) => { setArtifactLibraryOpen(false); setActiveArtifact(artifact); }} onClose={() => setArtifactLibraryOpen(false)} /> : activeArtifact?.artifactType === 'scarlet.schedule-options.v1' ? <ScheduleArtifactPane artifact={activeArtifact} onClose={() => setActiveArtifact(null)} /> : activeArtifact ? <StructuredArtifactPane artifact={activeArtifact} onClose={() => setActiveArtifact(null)} /> : null}
          </div>
        </ActivityVisibilityContext.Provider>
      </ArtifactWorkspaceContext.Provider>
      </ResponseTimestampContext.Provider>
      </AssistantRunClock>
    </AssistantRuntimeProvider>
  );
}

export function AssistantWorkspace({ requestedThreadId, initialPrompt, autoSend, selectedScheduleId, selectedDegreePlanId, sourceRoute, activeTerm, selectedCourseIds, visibleSectionIds, selectedEntity, availablePageActions, visibleView }: WorkspaceHandoff & { requestedThreadId?: string; initialPrompt?: string; autoSend?: boolean }) {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const creatingThreadRef = useRef(false);
  const [conversations, setConversations] = useState<AssistantConversationSummary[]>([]);
  const [artifacts, setArtifacts] = useState<AssistantArtifactLibraryItem[]>([]);
  const [artifactError, setArtifactError] = useState<string>();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; threadId: string; messages: UIMessage[] }>({ kind: 'loading' });

  const refreshArtifacts = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('ai_agent_artifacts')
      .select('id, conversation_id, artifact_type, data, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) {
      setArtifactError('Your saved drafts could not be loaded. Your conversations are still available.');
      return;
    }
    setArtifacts((data ?? []).flatMap((row) => {
      const artifact = parseStoredAssistantArtifact(row);
      return artifact ? [artifact] : [];
    }));
    setArtifactError(undefined);
  };

  const refreshConversations = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('ai_conversations')
      .select('id, title, updated_at')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false })
      .limit(50);
    if (!error) setConversations((data ?? []).map((row) => ({ id: row.id, title: row.title, updatedAt: row.updated_at })));
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      router.replace('/login?redirect=%2Fai-assistant');
      return;
    }

    let cancelled = false;
    const prepareThread = async () => {
      const session = await cachedGetSession(0);
      if (!session?.access_token) throw new Error('Authenticated session is unavailable');
      const { data: conversationRows, error: conversationError } = await supabase
        .from('ai_conversations')
        .select('id, title, updated_at')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(50);
      if (conversationError) throw conversationError;
      if (!cancelled) setConversations((conversationRows ?? []).map((row) => ({ id: row.id, title: row.title, updatedAt: row.updated_at })));
      const { data: artifactRows, error: artifactQueryError } = await supabase
        .from('ai_agent_artifacts')
        .select('id, conversation_id, artifact_type, data, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (!cancelled) {
        if (artifactQueryError) {
          setArtifacts([]);
          setArtifactError('Your saved drafts could not be loaded. Your conversations are still available.');
        } else {
          setArtifacts((artifactRows ?? []).flatMap((row) => {
            const artifact = parseStoredAssistantArtifact(row);
            return artifact ? [artifact] : [];
          }));
          setArtifactError(undefined);
        }
      }
      if (!requestedThreadId) {
        const { data: emptyConversationRows, error: emptyConversationError } = await supabase
          .from('ai_conversations')
          .select('id, title, updated_at, messages')
          .eq('user_id', user.id)
          .eq('title', 'New conversation')
          .order('updated_at', { ascending: false })
          .limit(20);
        if (emptyConversationError) throw emptyConversationError;
        const reusableThreadId = findReusableEmptyConversation(emptyConversationRows ?? []);
        if (reusableThreadId) {
          const params = workspaceParams(reusableThreadId);
          router.replace(`/ai-assistant?${params.toString()}`);
          return;
        }
        if (creatingThreadRef.current) return;
        creatingThreadRef.current = true;
        const { data, error } = await supabase.from('ai_conversations').insert({ user_id: user.id, title: 'New conversation', messages: [] }).select('id').single();
        if (error || !data) throw error ?? new Error('Conversation was not created');
        const params = workspaceParams(data.id);
        router.replace(`/ai-assistant?${params.toString()}`);
        return;
      }

      const { data, error } = await supabase.from('ai_conversations').select('id, messages').eq('id', requestedThreadId).eq('user_id', user.id).single();
      if (error || !data) throw error ?? new Error('Conversation was not found');
      const stored = Array.isArray(data.messages) ? (data.messages as StoredAssistantMessage[]) : [];
      if (!cancelled) {
        setState({
          kind: 'ready',
          threadId: data.id,
          messages: normalizeStoredAssistantMessages(stored),
        });
      }
    };

    prepareThread().catch(() => {
      if (!cancelled)
        setState({
          kind: 'error',
          message: 'Your private conversation could not be prepared.',
        });
    });
    return () => {
      cancelled = true;
    };
  }, [activeTerm, authLoading, autoSend, availablePageActions, initialPrompt, requestedThreadId, router, selectedCourseIds, selectedDegreePlanId, selectedEntity, selectedScheduleId, sourceRoute, user, visibleSectionIds]);

  const openConversation = (threadId: string) => {
    setHistoryOpen(false);
    setState({ kind: 'loading' });
    const params = workspaceParams(threadId);
    router.push(`/ai-assistant?${params.toString()}`);
  };

  const createConversation = async () => {
    if (!user || creatingConversation) return;
    setCreatingConversation(true);
    try {
      const { data, error } = await supabase.from('ai_conversations').insert({ user_id: user.id, title: 'New conversation', messages: [] }).select('id').single();
      if (error || !data) throw error ?? new Error('Conversation was not created');
      openConversation(data.id);
    } finally {
      setCreatingConversation(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--ss-surface)] " data-assistant-workspace>
      <section className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-[var(--ss-border)]" aria-labelledby="assistant-v2-heading">
        <h1 id="assistant-v2-heading" className="sr-only">Ask Scarlet Sync</h1>
        {state.kind === 'ready' ? (
          <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
            <ConversationHistory conversations={conversations} activeId={state.threadId} onSelect={openConversation} onNew={() => void createConversation()} mobileOpen={historyOpen} onMobileClose={() => setHistoryOpen(false)} />
            <AssistantThread
              key={state.threadId}
              threadId={state.threadId}
              initialMessages={state.messages}
              initialPrompt={initialPrompt}
              autoSend={autoSend}
              selectedScheduleId={selectedScheduleId}
              selectedDegreePlanId={selectedDegreePlanId}
              sourceRoute={sourceRoute}
              activeTerm={activeTerm}
              selectedCourseIds={selectedCourseIds}
              visibleSectionIds={visibleSectionIds}
              selectedEntity={selectedEntity}
              availablePageActions={availablePageActions}
              visibleView={visibleView}
              artifacts={artifacts}
              artifactError={artifactError}
              onOpenHistory={() => setHistoryOpen(true)}
              onArtifactsChanged={() => void refreshArtifacts()}
              onConversationsChanged={() => void refreshConversations()}
              onPromptConsumed={() => {
                const params = workspaceParams(state.threadId);
                router.replace(`/ai-assistant?${params.toString()}`, { scroll: false });
              }}
            />
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 place-items-center p-6">
            <OperationalState kind={state.kind === 'error' ? 'hard-failure' : 'initial-loading'} title={state.kind === 'error' ? 'Scarlet is unavailable' : 'Preparing your conversation'} description={state.kind === 'error' ? state.message : 'Loading your private assistant thread.'} />
          </div>
        )}
      </section>
    </div>
  );
}
